import React from 'react';
import SEO from './SEO';

const PrivacyPolicy = () => {
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'PrivacyPolicy',
        name: 'Privacy Policy',
        url: 'https://www.anmatics.com/privacy-policy',
        publisher: {
            '@type': 'Organization',
            name: 'Anmatics Technologies',
            url: 'https://www.anmatics.com',
        },
        dateModified: '2026-07-06',
    };

    return (
        <>
            <SEO
                title="Privacy Policy | Anmatics"
                description="Read the Anmatics privacy policy covering contact forms, consultation requests, LinkedIn ad submissions, cookies, data usage, sharing, retention, and user rights."
                path="/privacy-policy"
                jsonLd={jsonLd}
            />

            <section className="privacy-policy-page">
                <div className="container">
                    <div className="privacy-policy-hero">
                        <span className="section-kicker">Privacy Policy</span>
                        <h1>Privacy Policy</h1>
                        <p>
                            This policy explains how Anmatics collects, uses, protects, and handles personal
                            information shared through our website, contact forms, consultation requests, and
                            advertising campaigns.
                        </p>
                        <div className="privacy-policy-meta">Last updated: July 6, 2026</div>
                    </div>

                    <div className="privacy-policy-content">
                        <section>
                            <h2>1. Who We Are</h2>
                            <p>
                                Anmatics Technologies is a software development and technology company based in
                                Gujarat, India. We provide software engineering, SaaS product development,
                                automation, AI-ready systems, web applications, mobile applications, cloud/API
                                engineering, and related technology services.
                            </p>
                            <p>
                                For privacy-related questions, you can contact us at{' '}
                                <a href="mailto:chandrakant.namera@anmatics.com">
                                    chandrakant.namera@anmatics.com
                                </a>{' '}
                                or call <a href="tel:+918866899005">+91 88668 99005</a>.
                            </p>
                        </section>

                        <section>
                            <h2>2. Information We Collect</h2>
                            <p>
                                We may collect information that you choose to share with us, including your name,
                                work email address, mobile number, company name, project requirement, message
                                details, preferred contact method, and any information included in a form,
                                email, phone call, meeting request, or advertising lead form.
                            </p>
                            <p>
                                We may also collect limited technical information such as browser type, device
                                type, pages visited, approximate location, referral source, date and time of
                                visit, and similar usage data through standard analytics, cookies, or website
                                logs.
                            </p>
                        </section>

                        <section>
                            <h2>3. LinkedIn Ads and Lead Forms</h2>
                            <p>
                                If you interact with an Anmatics advertisement on LinkedIn or submit a LinkedIn
                                Lead Gen Form, we may receive the information you provide through that form,
                                such as your name, email address, phone number, job title, company name, and
                                business requirement. We use this information only to respond to your inquiry,
                                qualify the request, schedule a discussion, and provide relevant business
                                information about our services.
                            </p>
                            <p>
                                LinkedIn may process your information according to its own privacy policy and
                                platform terms. We recommend reviewing LinkedIn's privacy information when using
                                LinkedIn services.
                            </p>
                        </section>

                        <section>
                            <h2>4. How We Use Information</h2>
                            <ul>
                                <li>To respond to inquiries, consultation requests, and service questions.</li>
                                <li>To understand your project requirement and suggest practical next steps.</li>
                                <li>To schedule calls, meetings, demos, or technical discussions.</li>
                                <li>To prepare proposals, estimates, agreements, or service communication.</li>
                                <li>To improve our website, campaigns, content, and user experience.</li>
                                <li>To protect our website, systems, users, and business operations.</li>
                                <li>To comply with legal, tax, accounting, or regulatory obligations.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>5. Cookies and Analytics</h2>
                            <p>
                                Our website may use cookies, analytics tools, advertising pixels, or similar
                                technologies to understand website performance, visitor behavior, campaign
                                effectiveness, and general traffic trends. You can control or block cookies
                                through your browser settings. Some website features may not work as expected if
                                cookies are disabled.
                            </p>
                        </section>

                        <section>
                            <h2>6. How We Share Information</h2>
                            <p>
                                We do not sell personal information. We may share limited information with
                                trusted service providers who help us operate our website, manage hosting,
                                process contact forms, run analytics, deliver email, manage ads, or support
                                business operations. These providers are expected to handle information securely
                                and only for the purpose of providing their services.
                            </p>
                            <p>
                                We may also disclose information when required by law, legal process, government
                                request, or to protect our rights, safety, users, systems, or business.
                            </p>
                        </section>

                        <section>
                            <h2>7. Data Retention</h2>
                            <p>
                                We keep personal information only for as long as reasonably necessary for the
                                purpose it was collected, including responding to inquiries, maintaining business
                                records, handling ongoing relationships, resolving disputes, and meeting legal or
                                accounting requirements. When information is no longer needed, we may delete,
                                anonymize, or securely archive it.
                            </p>
                        </section>

                        <section>
                            <h2>8. Data Security</h2>
                            <p>
                                We use reasonable administrative, technical, and organizational safeguards to
                                protect personal information from unauthorized access, misuse, loss, or
                                disclosure. However, no internet transmission, email communication, or electronic
                                storage method can be guaranteed to be completely secure.
                            </p>
                        </section>

                        <section>
                            <h2>9. Your Choices and Rights</h2>
                            <p>
                                You may request access, correction, update, or deletion of personal information
                                that you have shared with us, subject to applicable legal and business
                                requirements. You may also ask us to stop contacting you for marketing or
                                business development communication.
                            </p>
                            <p>
                                To make a privacy request, email{' '}
                                <a href="mailto:chandrakant.namera@anmatics.com">
                                    chandrakant.namera@anmatics.com
                                </a>
                                .
                            </p>
                        </section>

                        <section>
                            <h2>10. Third-Party Links</h2>
                            <p>
                                Our website may link to third-party websites, platforms, or services. We are not
                                responsible for the privacy practices, security, or content of those third-party
                                websites. Please review their privacy policies before sharing information with
                                them.
                            </p>
                        </section>

                        <section>
                            <h2>11. Children's Privacy</h2>
                            <p>
                                Our website and services are intended for business users and are not directed to
                                children. We do not knowingly collect personal information from children.
                            </p>
                        </section>

                        <section>
                            <h2>12. Updates to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time to reflect changes in our
                                practices, services, legal requirements, or business operations. The updated
                                version will be posted on this page with a revised last updated date.
                            </p>
                        </section>

                        <section>
                            <h2>13. Contact Information</h2>
                            <p>
                                Anmatics Technologies
                                <br />
                                Head Office: 203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road,
                                Rajkot - 360005, India
                                <br />
                                Branch Office: 207, Second Floor, City Mall, Nr. Bhaktinagar Bypass, Sanala
                                Road, Morbi, Gujarat - 363641, India
                                <br />
                                Email:{' '}
                                <a href="mailto:chandrakant.namera@anmatics.com">
                                    chandrakant.namera@anmatics.com
                                </a>
                                <br />
                                Phone: <a href="tel:+918866899005">+91 88668 99005</a>
                            </p>
                        </section>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PrivacyPolicy;
